import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, TrendingUp, TrendingDown, Minus, Target, ShieldCheck, MapPin,
  Smile, Frown, LifeBuoy, Megaphone, Activity, Users,
} from "lucide-react";
import api from "../lib/api";
import { Label, InfoBadge } from "./common";

/** Benchmark + Industry KPI block used on both the Corporate Dashboard
 *  (compact variant) and the Benchmarking page (full variant).
 *
 *  Variants:
 *    - "market"      → Google / Area / Segment (no SSI, no industry KPIs)
 *    - "full"        → Servantage Index + Market + Industry KPI grid
 */
export default function BenchmarkKpis({ variant = "full", title }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/locations/benchmarks/overview?months=6")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err) return null;
  if (!data) return <div className="text-sm text-slate-400">Loading benchmarks…</div>;
  if (!data.configured) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4" data-testid="bench-kpis-dormant">
        <div className="flex items-start gap-3">
          <MapPin className="mt-0.5 h-4 w-4 text-slate-400" />
          <div>
            <Label>{title || "Market comparison"}</Label>
            <p className="mt-1 text-sm text-slate-600">
              Add your Google Maps API key to unlock market benchmarks and industry KPIs.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const p = data.portfolio || {};
  const k = data.kpis || {};
  // Prefer the canonical SSI (same number as the dashboard gauge). Fall back
  // to the 6-month rolling average when no feedback exists yet.
  const ssi5 = p.ssi5 ?? p.avg_ssi5;
  const ssiPercent = p.ssi_percent ?? p.avg_ssi;
  const google = p.avg_google;
  const area = p.avg_area;
  const segment = p.avg_segment;
  // Delta semantics: `benchmark - ssi`. Positive = benchmark above SSI (you
  // trail), negative = benchmark below SSI (you lead).
  const vsSsi = (benchmark) => (benchmark != null && ssi5 != null)
    ? Number((benchmark - ssi5).toFixed(2))
    : null;

  // Market tiles (Google / Area / Segment) — appear in every variant.
  const marketTiles = [
    {
      label: "Google rating",
      value: google,
      sub: `${(p.total_reviews || 0).toLocaleString()} reviews · ${p.stores_linked} stores`,
      accent: "#F59E0B",
      icon: Star,
      momPct: p.mom_google_pct,
      vs: vsSsi(google),
      testId: "bk-google",
      onClick: () => navigate("/app/reviews?source=google&refresh=1"),
      help: {
        title: "Google rating",
        text: "Pooled Google star rating across every store you've linked. Review counts are summed. Updated weekly from Google's Places API. Benchmark vs your Servantage Index (SSI) shown below.",
      },
    },
    {
      label: "Area avg (10 km)",
      value: area,
      sub: "same category · 10 km peers",
      accent: "#10B981",
      icon: Target,
      vs: vsSsi(area),
      testId: "bk-area",
      onClick: () => navigate("/app/benchmarking?refresh=1#area"),
      help: {
        title: "Area average (10 km)",
        text: "Average Google rating of same-category businesses within a 10 km radius of each of your stores. Shows how your stores stack up against the direct catchment competition.",
      },
    },
    {
      label: "Segment avg",
      value: segment,
      sub: "same category · country-wide",
      accent: "#0EA5E9",
      icon: Target,
      vs: vsSsi(segment),
      testId: "bk-segment",
      onClick: () => navigate("/app/benchmarking?refresh=1#segment"),
      help: {
        title: "Segment average",
        text: "Average Google rating of same-category businesses across the country. Broader benchmark that reflects how your segment is performing overall.",
      },
    },
  ];

  // Servantage Index tile — only in "full" variant to avoid dashboard dup.
  const ssiTile = {
    label: "Servantage Index",
    value: ssi5,
    sub: ssiPercent != null ? `${ssiPercent}/100 · ${(p.verified_interactions || 0).toLocaleString()} verified` : "no in-app ratings yet",
    accent: "#4338CA",
    icon: ShieldCheck,
    momPct: p.mom_internal_pct,
    testId: "bk-ssi",
    onClick: () => navigate("/app/reviews?source=internal"),
    isBaseline: true,
    help: {
      title: "Servantage Service Index (SSI)",
      text: "Your weighted service quality score (0–100). Combines rating, touchpoint sentiment, service recovery and trust signals from verified in-app feedback. This is the baseline every market comparison is measured against.",
    },
  };

  const tiles = variant === "full" ? [ssiTile, ...marketTiles]
    : variant === "market" ? [ssiTile, ...marketTiles]
    : marketTiles;
  const headerLabel = title || (variant === "full"
    ? "Servantage Index vs the market"
    : "External market comparison");
  const headerSub = variant === "full"
    ? "How your service ranks against Google, 10 km peers and country-wide segment"
    : variant === "market"
      ? "Your Servantage Index vs Google, 10 km peers and your country-wide segment"
      : "Independent view from Google, your area and your segment";

  return (
    <div className="space-y-4" data-testid={`bench-kpis-${variant}`}>
      <div>
        <Label>{headerLabel}</Label>
        <p className="mt-0.5 font-head text-lg font-semibold text-slate-900">{headerSub}</p>
      </div>

      <div className={`grid gap-3 ${variant === "full" || variant === "market" ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
        {tiles.map((t) => <Tile key={t.label} {...t} />)}
      </div>

      {variant === "full" && (
        <IndustryKpis k={k} p={p} navigate={navigate} />
      )}
    </div>
  );
}

/* ================= Industry KPI grid ================= */
function IndustryKpis({ k, p, navigate }) {
  if (!k || Object.keys(k).length === 0) return null;

  const kpis = [
    {
      label: "CSAT",
      value: k.csat_pct != null ? `${k.csat_pct.toFixed(1)}%` : "—",
      sub: "share of 4-5★ this month",
      delta: k.csat_mom_delta,
      deltaSuffix: "pts",
      icon: Smile,
      accent: "#10B981",
      goodIfPositive: true,
      hint: "Industry benchmark: 80%+ is top-tier retail service.",
      onClick: () => navigate("/app/reviews?star=5"),
    },
    {
      label: "Detractor rate",
      value: k.detractor_pct != null ? `${k.detractor_pct.toFixed(1)}%` : "—",
      sub: "share of 1-2★ this month",
      delta: k.detractor_mom_delta,
      deltaSuffix: "pts",
      icon: Frown,
      accent: "#DC2626",
      goodIfPositive: false,
      hint: "Target < 5%. Each detractor costs ~3× an acquisition.",
      onClick: () => navigate("/app/reviews?star=1"),
    },
    {
      label: "Recovery rate",
      value: k.recovery_rate_pct != null ? `${k.recovery_rate_pct.toFixed(0)}%` : "—",
      sub: `${k.cases_closed_60d || 0} closed / ${(k.cases_closed_60d || 0) + (k.cases_open_60d || 0)} opened (60d)`,
      icon: LifeBuoy,
      accent: "#4338CA",
      hint: "World-class is > 85% within 48 h.",
      onClick: () => navigate("/app/recovery"),
    },
    {
      label: "Share of Voice",
      value: k.share_of_voice_pct != null ? `${k.share_of_voice_pct.toFixed(0)}%` : "—",
      sub: "vs area peer review volume",
      icon: Megaphone,
      accent: "#F59E0B",
      hint: "At 100% you have the same review flow as your average 2 km peer. Below means fewer customers are talking about you.",
      onClick: () => navigate("/app/reviews?source=google&refresh=1"),
    },
    {
      label: "Review velocity",
      value: k.review_velocity != null ? `${k.review_velocity > 0 ? "+" : ""}${k.review_velocity}` : "building…",
      sub: "net new Google reviews MoM",
      icon: Activity,
      accent: "#0EA5E9",
      hint: "Positive momentum = you're staying top-of-mind. Measured once we have 2 months of snapshots.",
    },
    {
      label: "Portfolio variance",
      value: p.portfolio_variance != null ? `±${p.portfolio_variance.toFixed(2)}` : "—",
      sub: "star-rating spread across stores",
      icon: Users,
      accent: "#64748B",
      hint: "Lower is tighter — your worst store is close to your best. Above ±0.5 means the gap is large enough that one weak store is dragging the group.",
    },
  ];

  return (
    <div className="space-y-3">
      <div>
        <Label>Industry Health KPIs</Label>
        <p className="mt-0.5 font-head text-base font-semibold text-slate-900">Service quality best practices</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="industry-kpis">
        {kpis.map((kp) => <IndustryKpiTile key={kp.label} {...kp} />)}
      </div>
    </div>
  );
}

function IndustryKpiTile({ label, value, sub, delta, deltaSuffix, icon: Icon, accent, goodIfPositive = true, hint, onClick }) {
  const good = delta == null ? null : (goodIfPositive ? delta > 0 : delta < 0);
  const deltaClass = good == null ? "text-slate-400" : good ? "text-emerald-600" : "text-red-600";
  const Arrow = delta == null ? Minus : (delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus);
  const clickable = typeof onClick === "function";
  const onKey = clickable
    ? (e) => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } }
    : undefined;
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={onKey}
      className={`block w-full rounded-xl border border-slate-200 bg-white p-4 text-left ${clickable ? "cursor-pointer transition hover:border-indigo-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-1 min-w-0">
          <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
          {hint && <InfoBadge title={label} text={hint} testId={`industry-${label.toLowerCase().replace(/\s+/g, "-")}-info`} />}
        </span>
        {Icon && <Icon className="h-3.5 w-3.5" style={{ color: accent }} />}
      </div>
      <p className="mt-2 font-head text-2xl font-bold" style={{ color: accent }}>{value}</p>
      <p className="text-[11px] text-slate-500">{sub}</p>
      {delta != null && (
        <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${deltaClass}`}>
          <Arrow className="h-3 w-3" />
          {delta > 0 ? "+" : ""}{delta.toFixed(1)}{deltaSuffix || ""} vs last month
        </div>
      )}
    </div>
  );
}

/* ================= Market comparison tile ================= */
function Tile({ label, value, sub, accent, icon: Icon, momPct, vs, testId, onClick, isBaseline, help }) {
  const display = value != null && !Number.isNaN(value) ? Number(value).toFixed(2) : "—";
  const momArrow = momPct == null ? Minus : (momPct > 0 ? TrendingUp : momPct < 0 ? TrendingDown : Minus);
  const momTint = momPct == null ? "text-slate-400" : momPct > 0 ? "text-emerald-600" : momPct < 0 ? "text-red-600" : "text-slate-400";
  const clickable = typeof onClick === "function";
  // Use a role=button div (not a <button>) so the InfoBadge's inner <button> doesn't
  // nest interactive controls — avoids the hydration/a11y warning.
  const onKey = clickable
    ? (e) => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } }
    : undefined;

  // SSI tile is the baseline — show a chip instead of a delta.
  // Positive `vs` means the benchmark is above SSI (you trail = red).
  // Negative `vs` means the benchmark is below SSI (you lead = green).
  let vsLabel = null;
  let vsClass = "";
  if (!isBaseline && vs != null) {
    if (vs > 0) {
      vsLabel = `You trail by ${vs.toFixed(2)}★`;
      vsClass = "text-red-600";
    } else if (vs < 0) {
      vsLabel = `You lead by ${Math.abs(vs).toFixed(2)}★`;
      vsClass = "text-emerald-600";
    } else {
      vsLabel = "On par";
      vsClass = "text-slate-500";
    }
  }

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={onKey}
      className={`block w-full rounded-xl border ${isBaseline ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200 bg-white"} p-4 text-left ${clickable ? "cursor-pointer transition hover:border-indigo-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 min-w-0">
          <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {label}{isBaseline && <span className="ml-1 rounded bg-indigo-600 px-1 py-0.5 text-[9px] text-white">BASELINE</span>}
          </span>
          {help && <InfoBadge title={help.title} text={help.text} testId={`${testId}-info`} />}
        </span>
        {Icon && <Icon className="h-3.5 w-3.5" style={{ color: accent }} />}
      </div>

      <p className="mt-2 flex items-end gap-1">
        <span className="font-head text-3xl font-bold" style={{ color: accent }}>{display}</span>
        {value != null && <Star className="mb-1.5 h-3 w-3 text-amber-400" fill="currentColor" />}
      </p>
      <p className="text-[11px] text-slate-500">{sub}</p>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className={`inline-flex items-center gap-1 font-semibold ${momTint}`} title="Month-over-month change">
          {React.createElement(momArrow, { className: "h-3 w-3" })}
          {momPct == null ? "—" : `${momPct > 0 ? "+" : ""}${momPct.toFixed(1)}% MoM`}
        </span>
        {vsLabel && (
          <span className={`font-semibold ${vsClass}`} title="Compared to your Servantage Index">
            {vsLabel}
          </span>
        )}
      </div>
    </div>
  );
}
